import React from "react";
import { FaMapMarkerAlt, FaStar } from "react-icons/fa";
import { MdGroups } from "react-icons/md";
import { formatDistanceKm } from "../../utils/calculateDistance.js";

const currency = new Intl.NumberFormat("en-IN");
// Number(null) === 0, which passes Number.isFinite — so a plain Number.isFinite(Number(value))
// check never falls back for null and silently renders "0" instead of the intended fallback.
const formatNumber = (value, fallback = 0) =>
  currency.format(value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? fallback : Number(value));
const formatAddress = (address, banquet) => {
  if (typeof address === "string") return address;
  if (address && typeof address === "object") {
    return [address.line1, address.locality, address.city, address.pincode].filter(Boolean).join(", ");
  }
  return [banquet.locality, banquet.city].filter(Boolean).join(", ");
};

const infoItems = (banquet, distanceKm) => [
  { label: "Guest Capacity", value: `${formatNumber(banquet.guestMin, 50)} - ${formatNumber(banquet.guestMax, 250)} guests`, tone: "emerald" },
  { label: "Veg Price Per Plate", value: banquet.vegPrice ? `Veg: Rs. ${formatNumber(banquet.vegPrice)} per plate` : "Veg price on request", tone: "amber" },
  { label: "Non-Veg Price Per Plate", value: banquet.nonVegPrice ? `Non-Veg: Rs. ${formatNumber(banquet.nonVegPrice)} per plate` : "Non-Veg not available", tone: "sky" },
  { label: "Address", value: formatAddress(banquet.address, banquet), tone: "slate" },
].filter(Boolean);

const toneStyles = {
  emerald: "border-emerald-200 bg-emerald-50",
  amber: "border-amber-200 bg-amber-50",
  sky: "border-sky-200 bg-sky-50",
  slate: "border-slate-200 bg-slate-50",
};

export default function BanquetSummary({ banquet, distanceKm }) {
  const tags = Array.isArray(banquet.tags) ? banquet.tags : [];

  return (
    <section className="min-w-0 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_28px_90px_-50px_rgba(15,23,42,0.7)] sm:rounded-[30px]">
      <div className="h-2 bg-gradient-to-r from-amber-300 via-[#b9852d] to-emerald-500" />
      <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="break-words text-xs font-black uppercase tracking-[0.14em] text-[#9a6515] sm:tracking-[0.28em]">{banquet.venueType || "Premium venue"}</p>
          <h1 className="mt-2 break-words text-2xl font-black tracking-tight text-slate-950 sm:text-5xl">{banquet.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-600">
            <span className="inline-flex min-w-0 items-center gap-2">
              <FaMapMarkerAlt className="shrink-0 text-emerald-600" />
              <span className="min-w-0 break-words">{distanceKm !== null ? banquet.locality : `Locality: ${banquet.locality || banquet.city || "Not set"}`}</span>
              {distanceKm !== null && <span className="text-sky-700">{formatDistanceKm(distanceKm)}</span>}
            </span>
            <span className="inline-flex items-center gap-2"><MdGroups className="shrink-0 text-emerald-600" /> {formatNumber(banquet.guestMin, 50)}-{formatNumber(banquet.guestMax, 250)} guests</span>
            <span className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 font-bold text-slate-900"><FaStar className="text-amber-400" /> {banquet.googleRating || "New"}</span>
          </div>
          <p className="mt-4 max-w-3xl text-base font-medium leading-7 text-slate-700">{banquet.shortDescription}</p>
        </div>
        <div className="flex w-full flex-col gap-3 sm:w-auto">
          <a
            href={banquet.mapUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
          >
            View on Map
          </a>
          <a
            href={banquet.mapUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Open in Google Maps
          </a>
        </div>
      </div>

      {/* Mobile (<640px): dense divided list instead of 4 stacked boxes. */}
      <div className="mt-6 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 sm:hidden">
        {infoItems(banquet, distanceKm).map((item) => (
          <div key={item.label} className="flex min-w-0 items-baseline justify-between gap-3 px-3 py-2">
            <p className="shrink-0 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-500">{item.label}</p>
            <p className="min-w-0 break-words text-right text-xs font-bold leading-5 text-slate-950">{item.value}</p>
          </div>
        ))}
      </div>
      {/* Tablet/desktop (>=640px): unchanged boxed grid. */}
      <div className="mt-6 hidden min-w-0 gap-3 sm:grid sm:grid-cols-2 xl:grid-cols-3">
        {infoItems(banquet, distanceKm).map((item) => (
          <div key={item.label} className={`min-w-0 rounded-[24px] border px-4 py-4 shadow-sm ${toneStyles[item.tone]}`}>
            <p className="break-words text-xs font-black uppercase tracking-[0.12em] text-slate-500 sm:tracking-[0.2em]">{item.label}</p>
            <p className="mt-2 break-words text-base font-black leading-6 text-slate-950">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span key={tag} className="min-h-[40px] max-w-full rounded-2xl bg-slate-100 px-3 py-2 text-sm font-semibold leading-5 text-slate-700 sm:rounded-full">
            {tag}
          </span>
        ))}
      </div>
      </div>
    </section>
  );
}
